import React, { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useDispatch } from 'react-redux'

// antd
import {
    Layout,
    Divider,
    message,
    Popover,
    Space,
    Col,
    Image,
    Avatar,
    Row,
    Typography,
} from 'antd'
import { Icon } from '@iconify/react'

// navigation
import Navigation from '../navigation/navigation'

// components
import HeaderComponent from '../header/header'
import DeletePopUp from '../../components/deletePopUp/deletePopUp'
import SidebarProfilePhotoModal from './SidebarProfilePhotoModal'

// slices
import { isLoggedInLogin, signOut } from '../../modules/login/login.slice'

// routes
import {
    PRIVATE_ROUTES,
    PUBLIC_ROUTES,
    isProjectWidgetMode,
} from '../../routes'
import { showActionNotification } from '../../components/notification'
import { canViewReporting as canViewReportingPermission } from '../../helpers/reportsPermission'

// styles
import styles from '../../layouts/layouts.module.css'
import headerStyles from '../header/header.module.css'
import navStyles from '../navigation/navigation.module.css'
import ProjectsList from '../navigation/projectsList'
import logo from '../../assets/images/logo.png'
import co7Logo from '../../assets/svg/CO7 Logo.svg'

const { Sider, Content, Header } = Layout
const { Text } = Typography

const shouldHideSidebar = (pathname, search, userProfile) => {
    if (isProjectWidgetMode(search)) {
        return true
    }

    const isProfilePage = pathname.includes('profile')
    const isGlobalSettingsPage = pathname.includes('global-settings')
    const isUsersPage = pathname.includes('users')
    const isProjectReportsPage =
        /\/dashboard\/projects\/[^/]+\/reports(?:\/|$)/.test(pathname)
    const isReportsPage = pathname.includes('reports') && !isProjectReportsPage
    const isInquiriesPage = pathname.includes('inquiries')

    return (
        isProfilePage ||
        isGlobalSettingsPage ||
        isUsersPage ||
        isReportsPage ||
        isInquiriesPage
    )
}

const shouldHideHeader = (pathname, search, userProfile) => {
    if (isProjectWidgetMode(search)) {
        return true
    }

    return pathname.includes('global-settings') && userProfile?.isSupportUser
}

const SideLayout = ({ userProfile }) => {
    const params = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [collapsed, setCollapsed] = useState(false)
    const [openProfile, setOpenProfile] = useState(false)
    const [profilePhotoModalOpen, setProfilePhotoModalOpen] = useState(false)
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteButtonLoading, setDeleteButtonLoading] = useState(false)

    const isClientUser =
        userProfile?.isSupportUser && !userProfile?.isExistingUser
    const isSuperSupportAdmin =
        userProfile?.isSupportUser && userProfile?.isSuperAdmin
    const isReportingActive = params.pathname.startsWith(
        PRIVATE_ROUTES.reporting
    )
    const isCo7Active = params.pathname.startsWith(PRIVATE_ROUTES.co7)
    const isSupportUser = Boolean(userProfile?.isSupportUser)
    const isExistingUser = Boolean(userProfile?.isExistingUser)
    const canViewReporting = canViewReportingPermission(userProfile)
    // LOGOUT
    const logOut = async () => {
        setDeleteButtonLoading(true)
        const result = await dispatch(signOut())
        const data = result?.payload?.data
        if (data) {
            const { success, message: logOutMessage } = data
            if (success) {
                localStorage.removeItem('token')
                sessionStorage.removeItem('token')

                setDeleteButtonLoading(false)
                showActionNotification({
                    type: 'success',
                    message: logOutMessage,
                })
                const result = await dispatch(isLoggedInLogin())

                const data = result?.payload?.data
                if (data) {
                    const { success } = data
                    if (!success) {
                        navigate(PUBLIC_ROUTES.login)
                        window.localStorage.clear()
                    }
                }
            } else {
                setDeleteButtonLoading(false)
                message.error('Something went wrong, try again later.')
            }
        }
    }

    return (
        <>
            <DeletePopUp
                previewDelete={deleteModal}
                onDelete={logOut}
                loadingButton={deleteButtonLoading}
                onCancel={() => {
                    setDeleteButtonLoading(false)
                    setDeleteModal(false)
                }}
                modalTitle="Log Out"
                keyModal="logout"
            />
            {isClientUser ? (
                <SidebarProfilePhotoModal
                    open={profilePhotoModalOpen}
                    onClose={() => setProfilePhotoModalOpen(false)}
                    userProfile={userProfile}
                />
            ) : null}
            <Layout
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Header
                    className={headerStyles.modernHeader}
                    style={{
                        backgroundColor: '#011022',
                        position: 'relative',
                        zIndex: 1000,
                        flex: '0 0 48px',
                        padding: '9px 17px',
                        display: shouldHideHeader(
                            params.pathname,
                            params.search,
                            userProfile
                        )
                            ? 'none'
                            : 'flex',
                    }}
                >
                    {!shouldHideHeader(
                        params.pathname,
                        params.search,
                        userProfile
                    ) && (
                        <HeaderComponent
                            collapsed={collapsed}
                            setCollapsed={setCollapsed}
                        />
                    )}
                </Header>
                <Layout
                    style={{
                        flex: '1 1 auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            minHeight: 1,
                            flex: '1 1 0px',
                        }}
                    >
                        <Sider
                            width={240}
                            collapsedWidth={0}
                            trigger={null}
                            collapsible
                            collapsed={collapsed}
                            style={{
                                position: 'relative',
                                display: shouldHideSidebar(
                                    params.pathname,
                                    params.search,
                                    userProfile
                                )
                                    ? 'none'
                                    : 'block',
                                backgroundColor: '#011022',
                                transition:
                                    'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflow: 'hidden',
                                opacity: collapsed ? 0 : 1,
                            }}
                            className={styles.modernSidebar}
                        >
                            <div className={styles.sidebarMain}>
                                <div className={styles.sidebarLogoContainer}>
                                    <button
                                        type="button"
                                        className={styles.sidebarLogoButton}
                                        onClick={() =>
                                            navigate('/dashboard/home')
                                        }
                                        aria-label="Go to home"
                                    >
                                        <img
                                            src={logo}
                                            alt="Logo"
                                            className={styles.sidebarLogo}
                                        />
                                    </button>
                                </div>
                                <div className={styles.navigationContainer}>
                                    <Navigation userProfile={userProfile} />
                                </div>
                                {isSuperSupportAdmin ||
                                isSupportUser ||
                                canViewReporting ? (
                                    <>
                                        <Divider
                                            style={{
                                                margin: '0px 16px 8px 16px',
                                                borderColor: '#334155',
                                                width: 'auto',
                                                minWidth: 'auto',
                                            }}
                                        />
                                        {canViewReporting ? (
                                            <button
                                                type="button"
                                                className={`${navStyles.sidebarExtraNavItem} ${
                                                    isReportingActive
                                                        ? navStyles.sidebarExtraNavItemActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    navigate(
                                                        PRIVATE_ROUTES.reporting
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="mdi:chart-timeline-variant"
                                                    width={20}
                                                    height={20}
                                                    className={
                                                        navStyles.sidebarExtraNavIcon
                                                    }
                                                />
                                                <span>Reporting</span>
                                            </button>
                                        ) : null}
                                        {isSupportUser && isExistingUser ? (
                                            <button
                                                type="button"
                                                className={`${navStyles.sidebarExtraNavItem} ${
                                                    isCo7Active
                                                        ? navStyles.sidebarExtraNavItemActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    navigate(PRIVATE_ROUTES.co7)
                                                }
                                            >
                                                <img
                                                    src={co7Logo}
                                                    alt=""
                                                    className={
                                                        navStyles.sidebarExtraNavLogo
                                                    }
                                                />
                                                <span>CO7</span>
                                            </button>
                                        ) : null}
                                    </>
                                ) : null}
                                <ProjectsList collapsed={collapsed} />
                            </div>

                            {/* USER PROFILE SECTION AT BOTTOM */}

                            <div className={styles.sidebarProfileWrapper}>
                                <Popover
                                    styles={{
                                        body: {
                                            padding: 0,
                                            borderRadius: '12px',
                                        },
                                    }}
                                    getPopupContainer={() => document.body}
                                    open={openProfile}
                                    onOpenChange={setOpenProfile}
                                    content={
                                        <div
                                            className={
                                                headerStyles.profilePopover
                                            }
                                        >
                                            <div
                                                className={
                                                    headerStyles.profileHeader
                                                }
                                            >
                                                <Space size={'middle'}>
                                                    <Col>
                                                        {userProfile?.profilePicture ? (
                                                            <Image
                                                                src={
                                                                    userProfile?.profilePicture
                                                                }
                                                                height={40}
                                                                width={40}
                                                                rootClassName="headerImage"
                                                                style={{
                                                                    borderRadius:
                                                                        '50%',
                                                                    cursor: 'pointer',
                                                                }}
                                                                preview={false}
                                                            />
                                                        ) : (
                                                            <Avatar
                                                                size={'large'}
                                                                style={{
                                                                    backgroundColor:
                                                                        'rgb(0 10 129 / 25%)',
                                                                }}
                                                            >
                                                                {userProfile?.userName
                                                                    ? `${userProfile?.userName[0].toUpperCase()}${
                                                                          userProfile?.userName.split(
                                                                              ' '
                                                                          )[1] !==
                                                                          undefined
                                                                              ? userProfile?.userName
                                                                                    .split(
                                                                                        ' '
                                                                                    )[1][0]
                                                                                    .toUpperCase()
                                                                              : ''
                                                                      }`
                                                                    : ''}
                                                            </Avatar>
                                                        )}
                                                    </Col>
                                                    <Col>
                                                        <div
                                                            className={
                                                                headerStyles.profileName
                                                            }
                                                        >
                                                            {
                                                                userProfile?.userName
                                                            }
                                                        </div>
                                                        <div
                                                            className={
                                                                headerStyles.profileRole
                                                            }
                                                        >
                                                            {
                                                                userProfile
                                                                    ?.role
                                                                    ?.roleName
                                                            }
                                                        </div>
                                                        <div
                                                            className={
                                                                headerStyles.profileEmail
                                                            }
                                                        >
                                                            {userProfile?.email}
                                                        </div>
                                                    </Col>
                                                </Space>
                                            </div>
                                            {isClientUser ? (
                                                <>
                                                    <Divider
                                                        style={{ margin: 0 }}
                                                    />
                                                    <div
                                                        className={
                                                            headerStyles.profilePhotoCard
                                                        }
                                                        onClick={() => {
                                                            setOpenProfile(
                                                                false
                                                            )
                                                            setProfilePhotoModalOpen(
                                                                true
                                                            )
                                                        }}
                                                    >
                                                        <Row
                                                            align={'middle'}
                                                            style={{ gap: 10 }}
                                                        >
                                                            <Icon
                                                                icon="ion:camera-outline"
                                                                width={18}
                                                                height={18}
                                                                style={{
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                            <Text
                                                                className={
                                                                    headerStyles.menuText
                                                                }
                                                            >
                                                                Update profile
                                                                photo
                                                            </Text>
                                                        </Row>
                                                    </div>
                                                </>
                                            ) : null}
                                            <Divider style={{ margin: 0 }} />
                                            <div
                                                className={
                                                    headerStyles.logoutCard
                                                }
                                                onClick={() => {
                                                    setOpenProfile(false)
                                                    setDeleteModal(true)
                                                }}
                                            >
                                                <Row
                                                    align={'middle'}
                                                    style={{ gap: 10 }}
                                                >
                                                    <Icon
                                                        icon="ant-design:logout-outlined"
                                                        width={18}
                                                        height={18}
                                                        style={{
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Text
                                                        className={
                                                            headerStyles.logoutText
                                                        }
                                                    >
                                                        Log out
                                                    </Text>
                                                </Row>
                                            </div>
                                        </div>
                                    }
                                    trigger="click"
                                    arrow={false}
                                    placement="rightTop"
                                >
                                    <div className={styles.sidebarProfileCard}>
                                        {userProfile?.profilePicture ? (
                                            <Image
                                                src={
                                                    userProfile?.profilePicture
                                                }
                                                height={36}
                                                width={36}
                                                className={`headerImage ${headerStyles.userAvatar}`}
                                                style={{
                                                    borderRadius: '50%',
                                                    cursor: 'pointer',
                                                }}
                                                preview={false}
                                            />
                                        ) : (
                                            <Avatar
                                                size={'large'}
                                                className={
                                                    headerStyles.userAvatar
                                                }
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor:
                                                        'rgb(0 10 129 / 25%)',
                                                    minWidth: '36px',
                                                }}
                                            >
                                                {userProfile?.userName?.[0]?.toUpperCase() ||
                                                    'U'}
                                                {userProfile?.userName
                                                    ?.split(' ')?.[1]?.[0]
                                                    ?.toUpperCase() || ''}
                                            </Avatar>
                                        )}
                                        {!collapsed && (
                                            <div
                                                className={
                                                    styles.sidebarProfileInfo
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.sidebarProfileName
                                                    }
                                                >
                                                    {userProfile?.userName}
                                                </div>
                                                <div
                                                    className={
                                                        styles.sidebarProfileRole
                                                    }
                                                >
                                                    {userProfile?.isSuperAdmin
                                                        ? 'Super Admin'
                                                        : userProfile?.isSupportUser &&
                                                            !userProfile?.isExistingUser
                                                          ? 'Client'
                                                          : 'Member'}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Popover>
                            </div>
                        </Sider>

                        <Layout
                            style={{
                                flex: 1,
                                minHeight: 0,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            <Content
                                className={styles.contentWrapper}
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Outlet />
                            </Content>
                        </Layout>
                    </div>
                </Layout>
            </Layout>
        </>
    )
}

export default React.memo(SideLayout)
